import React, { useCallback, useEffect, useRef, useState } from "react";
import { BUILD_ID } from "../generated/buildVersion";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export default function VersionUpdateBanner() {
  const [availableVersion, setAvailableVersion] = useState("");
  const checkingRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (checkingRef.current || !BUILD_ID) return;

    checkingRef.current = true;

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) return;

      const data = await response.json();
      const remoteBuildId = String(data?.build_id || "").trim();

      if (remoteBuildId && remoteBuildId !== BUILD_ID) {
        setAvailableVersion(remoteBuildId);
      }
    } catch {
      // La plataforma sigue funcionando aunque la comprobación no responda.
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    checkVersion();

    const intervalId = window.setInterval(checkVersion, CHECK_INTERVAL_MS);

    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") checkVersion();
    };

    window.addEventListener("focus", checkVersion);
    window.addEventListener("pageshow", checkVersion);
    window.addEventListener("online", checkVersion);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", checkVersion);
      window.removeEventListener("pageshow", checkVersion);
      window.removeEventListener("online", checkVersion);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [checkVersion]);

  if (!availableVersion) return null;

  const applyUpdate = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("zedro_v", availableVersion);
    window.location.replace(url.toString());
  };

  return (
    <aside style={bannerStyle} role="status" aria-live="polite">
      <div style={copyWrapStyle}>
        <strong style={titleStyle}>Hay una actualización disponible</strong>
        <span style={copyStyle}>
          Actualiza para continuar usando la versión más reciente de ZEDRO
          Academy.
        </span>
      </div>

      <button type="button" onClick={applyUpdate} style={buttonStyle}>
        Actualizar ahora
      </button>
    </aside>
  );
}

const bannerStyle = {
  position: "fixed",
  left: "50%",
  bottom: 18,
  zIndex: 99990,
  width: "min(calc(100% - 24px), 760px)",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
  padding: "14px 16px",
  borderRadius: 18,
  background: "#ffffff",
  border: "1px solid rgba(40, 107, 148, 0.22)",
  boxShadow: "0 20px 55px rgba(12, 35, 52, 0.22)",
  fontFamily: "Questrial, sans-serif",
};

const copyWrapStyle = {
  display: "grid",
  gap: 4,
  minWidth: 0,
};

const titleStyle = {
  color: "#0f2f46",
  fontSize: 15,
};

const copyStyle = {
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.45,
};

const buttonStyle = {
  flexShrink: 0,
  minHeight: 42,
  padding: "0 17px",
  border: "none",
  borderRadius: 999,
  background: "#286B94",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};
